<template>
  <div class="container">
    <header class="header pb-2.5">
      <div class="title px-4 font-normal">Approvals</div>
      <div v-if="userName" class="user-name">{{ userName }}</div>
      <div class="flex px-4 pt-2.5">
        <div
          v-for="tab in approvalsTab"
          :key="tab.key"
          :class="[
            'header-btn flex items-center justify-center mr-2',
            { active: activeTab.tabKey === tab.tabKey },
          ]"
          @click="tabAction(tab)"
        >
          {{ tab.key }}
          <span v-if="taskCount">({{ getCount(tab) }})</span>
        </div>
      </div>
      <van-search
        class="mx-4 mt-2.5"
        v-model="storeName"
        placeholder="Search by project code, pos id or store name."
        @search="onSearch"
      >
        <template #left-icon></template>
        <template #right-icon>
          <van-icon name="search" @click="onSearch" />
        </template>
      </van-search>
    </header>
    <div class="list-view">
      <van-pull-refresh
        v-model="refreshing"
        @refresh="onRefresh"
        pulling-text="Pull down to refresh"
        loosing-text="Release to refresh"
        loading-text="Loading..."
        class="data-list"
      >
        <van-empty v-if="listdata.length === 0" description="No Data" />
        <div v-for="(item, index) in listdata" :key="index" :class="{ 'last-item': index === listdata.length - 1 }">
          <div class="gate-view px-4 mt-2 flex justify-between items-center">
            <span class="group-title" @click="taskNameAction(item.currentTaskName)">
              <van-icon v-if="gapList.includes(item.currentTaskName)" name="arrow-down" />
              <van-icon v-else name="arrow" />
              {{ item.currentTaskName }}（{{ item.count }}）
            </span>
            <div class="flex items-center shrink-0" @click.stop="toggleGroupSelect(item)">
              <div
                :class="[
                  'checkbox mr-2',
                  { active: isGroupSelected(item), partial: isGroupPartial(item) },
                ]"
              ></div>
              <div class="checkbox-text">Select All</div>
            </div>
          </div>
          <transition name="collapse">
            <div v-show="gapList.includes(item.currentTaskName)" class="group-wrapper">
              <transition-group name="list-fade" tag="div">
                <div
                  v-for="i in item.list"
                  :key="i.taskId"
                  class="mx-4 mt-2 l-item py-4 px-4"
                  @click="gotoDetail(i)"
                >
                  <div class="flex justify-between gap-x-8">
                    <div class="list-title">{{ i.storeNameLocal }}</div>
                  </div>
                  <div class="select-hit-area" @click.stop="selectAction(i.taskId)">
                    <div
                      class="select-item"
                      :class="{ active: selectListId.includes(i.taskId) }"
                    ></div>
                  </div>
                  <div class="flex justify-between gap-x-1 mt-2">
                    <div class="status">{{ i.currentTaskName }}</div>
                    <div class="status-tag shrink-0">{{ i.status }}</div>
                  </div>
                  <div class="flex info-item mt-2 pb-2">
                    <div class="flex-1 left-info pr-2">
                      <div class="info-text info-top">{{ i.accountGroup }}</div>
                      <div class="info-text">{{ i.clcProjectCode }}</div>
                    </div>
                    <div class="flex-1 shrink-0 pl-3">
                      <div class="info-text info-top">
                        Exec: {{ i.executionFlow }}, {{ i.fyQuarter }}
                      </div>
                      <div class="flex items-center info-text" v-show="i.remainingDays">
                        Remaining Time:
                        <div></div>
                        {{ i.remainingDays }} days
                      </div>
                    </div>
                  </div>
                </div>
              </transition-group>
            </div>
          </transition>
        </div>
      </van-pull-refresh>
    </div>
  </div>
  <div v-if="selectListId.length" class="footer-view px-8">
    <div class="footer-btn flex items-center justify-center" @click="handleApprove">Approve ({{ selectListId.length }})</div>
  </div>

  <GlobalDialog
    v-model:show="confirmDialogVisible"
    :message="confirmMessage"
    @confirm1="onConfirmApprove"
    @cancel="confirmDialogVisible = false"
  />
</template>

<script setup lang="ts">
import { showToast } from 'vant'
import { withLoading } from '@/utils/request'
import { getApprovalsList, getTaskCount, doTask, saveAndGetData } from '@/api/approvals'
import { approvalsTab } from '@/utils/config'
import type { IApprovalGroup, ITaskCount } from '@/types/approvals'
import { getCurrentUser } from '@/api/login'
import GlobalDialog from '@/components/GlobalDialog.vue'
import { doTaskClosure } from '@/api/closure'

const router = useRouter()
// approvalsTab顶部选项

// 记录并恢复上次的查询类别和搜索词
const LAST_TAB_KEY = 'approvals_last_tab_key'
const LAST_STORE_NAME = 'approvals_last_store_name'
const initTabKey = Number(sessionStorage.getItem(LAST_TAB_KEY))
const activeTab = ref<any>(approvalsTab.find((t) => t.tabKey === initTabKey) || approvalsTab[0])
const storeName = ref(sessionStorage.getItem(LAST_STORE_NAME) || '')
// 选中的内容
const selectListId = ref<string[]>([])

// 列表数据
const listdata = ref<IApprovalGroup[]>([])
// 列表task总数
const taskCount = ref<ITaskCount>({ applying: 0, closure: 0, dc: 0 })
const userName = ref('')
const refreshing = ref(false)
const confirmDialogVisible = ref(false)
const confirmMessage = computed(() => {
  const n = selectListId.value.length
  return `Please double confirm the operation for ${n} ${n === 1 ? 'store' : 'stores'}?`
})

const getCount = (tab: any) => {
  switch (tab.key) {
    case 'Applying':
      return taskCount.value.applying
    case 'D&C':
      return taskCount.value.dc
    case 'Closure':
      return taskCount.value.closure
    default:
      return 0
  }
}

// 分组的name,用来控制分组的展开收起
const gapList = ref<string[]>([])

// 切换顶部选项
const tabAction = (tab: any) => {
  activeTab.value = tab
  sessionStorage.setItem(LAST_TAB_KEY, String(tab.tabKey))
  resetData()
  getList()
}

// 搜索
const onSearch = () => {
  sessionStorage.setItem(LAST_STORE_NAME, storeName.value)
  resetData()
  getList()
}

// 重置数据
const resetData = () => {
  selectListId.value = []
}

// 组别全选判断
const isGroupSelected = (item: any) => {
  if (!item.list || !item.list.length) return false
  return item.list.every((i: any) => selectListId.value.includes(i.taskId))
}

// 组别部分选中判断
const isGroupPartial = (item: any) => {
  if (!item.list || !item.list.length) return false
  const count = item.list.filter((i: any) => selectListId.value.includes(i.taskId)).length
  return count > 0 && count < item.list.length
}

// 组别全选操作
const toggleGroupSelect = (item: any) => {
  const ids = item.list.map((i: any) => i.taskId)
  if (isGroupSelected(item)) {
    // uncheck all
    selectListId.value = selectListId.value.filter((id) => !ids.includes(id))
  } else {
    // check all
    const toAdd = ids.filter((id: any) => !selectListId.value.includes(id))
    selectListId.value.push(...toAdd)
  }
}

// 选择
const selectAction = (id: string) => {
  // 判断是否选中
  if (selectListId.value.includes(id)) {
    selectListId.value = selectListId.value.filter((item) => item !== id)
  } else {
    selectListId.value.push(id)
  }
}

// 点击分组
const taskNameAction = (key: string) => {
  if (gapList.value.includes(key)) {
    gapList.value = gapList.value.filter((item) => item !== key)
  } else {
    gapList.value.push(key)
  }
}
// 跳转详情
const gotoDetail = (item: any) => {
  const currentTaskName = item.currentTaskName || ''
  
  const allItemsInCurrentGroup = listdata.value
    .filter((group) => group.currentTaskName === currentTaskName)
    .flatMap((group) => group.list)
  
  const allTaskData = allItemsInCurrentGroup.map((it) => ({
    account: it.account,
    clcDoorCode: it.clcDoorCode,
    doorCode: it.doorCode,
    executionFlow: it.executionFlow,
    facadeFlag: it.facadeFlag,
    hubFlag: it.hubFlag,
    storeImage: it.storeImage,
    taskId: it.taskId,
  }))

  router.push({
    path: '/approvalsDetail',
    query: {
      batchId: item.batchId,
      taskId: item.taskId,
      clcDoorCode: item.clcDoorCode,
      clcProjectCode: item.clcProjectCode,
      storeNameLocal: item.storeNameLocal,
      currentTaskName: item.currentTaskName,
      allTaskData: JSON.stringify(allTaskData),
    },
  })
}

// 获取列表
const getList = async (showLoading = true) => {
  try {
    const res = await getApprovalsList(
      {
        processDefinitionKeys: activeTab.value.processDefinitionKeys,
        tabKey: activeTab.value.tabKey,
        storeName: storeName.value,
      },
      showLoading ? { loading: 'Loading...' } : undefined,
    )
    listdata.value = res.data
    gapList.value = []
  } catch (error) {
    console.error(error)
  }
}

const onRefresh = async () => {
  try {
    await getList(false)
    await getTotal()
  } finally {
    refreshing.value = false
  }
}

// 获取列表总数
const getTotal = async () => {
  try {
    const res = await getTaskCount({})
    taskCount.value = res.data
  } catch (error) {
    console.error(error)
  }
}

const fetchUser = async () => {
  try {
    if (sessionStorage.getItem('user_name')) {
      userName.value = sessionStorage.getItem('user_name') || ''
    } else {
      const result = await getCurrentUser()
      userName.value = String(result?.data?.user?.userName || '')
      sessionStorage.setItem('user_name', userName.value)
    }
  } catch {}
}

// 批量审批
const handleApprove = () => {
  confirmDialogVisible.value = true
}

const onConfirmApprove = async () => {
  const allItems = listdata.value.flatMap((g) => g.list)
  const selectedItems = allItems.filter((it) => selectListId.value.includes(it.taskId))

  // 提前做本地校验，避开 loading 生命周期，简化后续逻辑
  if (activeTab.value?.key === 'Closure') {
    if (!selectedItems.length) return
  } else if (activeTab.value?.key !== 'D&C') {
    for (const item of selectedItems) {
      const taskName = item.currentTaskName || ''
      const hasN6 = taskName.startsWith('N6')
      const hasK3 = taskName.startsWith('K3')
      if ((hasN6 || hasK3) && !item.franchise) {
        showToast(`Clc Project Code: ${item.clcProjectCode} Franchise is required`)
        return
      }
    }
  }

  try {
    await withLoading(
      async () => {
        if (activeTab.value?.key === 'Closure') {
          await Promise.all(
            selectedItems.map((it) =>
              doTaskClosure('complete', {
                // todo: 补充参数：id、taskDefinitionKey，这两个参数目前数据不存在
                taskId: it.taskId,
                processDefinitionKey: it.workflow,
              }),
            ),
          )
        } else {
          // D&C 只需完成任务，不调用 /app/activiti/save；Applying 才需要先 save
          if (activeTab.value?.key !== 'D&C') {
            const payload = selectedItems.map((item) => ({
              account: item.account,
              clcDoorCode: item.clcDoorCode,
              doorCode: item.doorCode,
              executionFlow: item.executionFlow,
              facadeFlag: item.facadeFlag,
              hubFlag: item.hubFlag,
              storeImage: item.storeImage,
              taskId: item.taskId,
            }))
            await saveAndGetData(payload)
          }
          await doTask('complete', selectListId.value)
        }
      },
      { message: 'Processing...', successMsg: 'Success' },
    )
    resetData()
    getList(false)
    getTotal()
  } catch (error) {
    console.error(error)
  }
}

onMounted(() => {
  const isWeComEnv = () => /wxwork/i.test(navigator.userAgent)
  const hasToken = () => document.cookie.split('; ').some((s) => s.startsWith('Authorization='))
  const init = () => {
    fetchUser()
    getList()
    getTotal()
  }
  if (!isWeComEnv() || hasToken()) {
    init()
  } else {
    const handler = () => {
      window.removeEventListener('wecom-auth-done', handler)
      init()
    }
    window.addEventListener('wecom-auth-done', handler)
  }
})
</script>
<style lang="scss" scoped>
@media (orientation: landscape) {
  .footer-view {
    zoom: 0.5;
  }
}

.container {
  width: 100%;
  height: 100%;
}
.header {
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 9;
  background-color: #fff;
}
.user-name {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 12px;
  color: #333;
}
.gate-view {
  font-size: 14px;
  color: #007eb4;
  font-weight: bold;
}
.group-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
}
.title {
  font-size: 18px;
  color: rgb(51, 51, 51);
  border: none;
}
.header-btn {
  height: 24px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 12px;
  color: rgb(29, 29, 29);
  border: 1px solid rgb(29, 29, 29);
  background-color: #fff;
  border-radius: 12px;
  /* background-color: rgb(29, 29, 29); */
}
.header-btn.active {
  background-color: rgb(29, 29, 29);
  color: #fff;
}

:deep(.van-search__content) {
  font-size: 13px;
  background-color: #fff;
}
:deep(.van-search) {
  height: 28px;
  padding: 0;
  // border: 1px solid rgb(243, 244, 246);
  // box-shadow: rgba(255, 255, 255, 0) 0px 0px 1px;
  line-height: 28px;
  .van-search__field,
  .van-search__content {
    height: 28px;
    padding: 0;
    margin: 0;
    line-height: 28px;
  }
  .van-search__field {
    border: 1px solid rgb(243, 244, 246);
    box-shadow: rgba(255, 255, 255, 0) 0px 0px 1px;
    border-radius: 8px;
  }
  .van-icon-search {
    font-size: 16px;
  }
}

.data-list {
  overflow-y: auto;
  height: calc(100vh - 100px - var(--van-tabbar-height));
  -ms-overflow-style: none;
  scrollbar-width: none;
}
:deep(.data-list)::-webkit-scrollbar {
  display: none;
}
.last-item {
  padding-bottom: 20px;
}
.checkbox {
  height: 13px;
  width: 13px;
  background-color: rgb(255, 255, 255);
  opacity: 1;
  border-width: 1px;
  border-color: rgb(119, 119, 119);
  border-style: solid;
  position: relative;
}
.checkbox.partial,
.checkbox.active {
  background-color: rgb(119, 119, 119);
}
// 选中状态
.select-item.active::after,
.checkbox.active::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  background-image: url('@/assets/gc-bi/icon/SVG/check.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}
// 部分选中
.checkbox.partial::after {
  content: '—';
  color: #fff;
  font-size: 12px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.checkbox-text {
  font-size: 13px;
  text-align: left;
  color: rgb(127, 127, 127);
  font-weight: bold;
}
.footer-view {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70px;
  background-color: #fff;
  border-top: 1px solid rgb(243, 244, 246);
  padding-bottom: env(safe-area-inset-bottom);
  z-index: 9;
}
.footer-btn {
  width: 280px;
  height: 45px;
  font-size: 16px;
  color: rgb(255, 255, 255);
  font-weight: bold;
  background-color: rgb(25, 190, 107);
  border-radius: 4.5px;
  transition: unset;
}
.list-view {
  height: 100vh;
  overflow-x: auto;
  scrollbar-width: none;
  padding-top: 100px;
  padding-bottom: calc(env(safe-area-inset-bottom) + var(--van-tabbar-height));
  box-shadow: -8px 0 12px rgba(0, 0, 0, 0.15);
}
.collapse-enter-from,
.collapse-leave-to {
  height: 0;
  opacity: 0;
}
.collapse-enter-to,
.collapse-leave-from {
  height: auto;
  opacity: 1;
}
.collapse-enter-active,
.collapse-leave-active {
  overflow: hidden;
  transition:
    height 0.25s ease,
    opacity 0.2s ease;
}
.list-fade-enter-from,
.list-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.list-fade-enter-active,
.list-fade-leave-active {
  transition: all 0.25s ease;
}

.list-fade-move {
  transition: transform 0.25s ease;
}

.gate-view {
  color: #007eb4;
  font-weight: bold;
}
.l-item {
  background-color: rgb(255, 255, 255);
  filter: drop-shadow(rgba(0, 0, 0, 0.15) 0px 3px 6px);
  border-radius: 12.5px;
  transition: unset;
  position: relative;
}
.select-item {
  width: 18px;
  height: 18px;
  border-radius: 9px;
  border: 1px solid rgb(119, 119, 119);
  background-color: #fff;
  position: relative;
}
.select-hit-area {
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  height: 50px;
  z-index: 1;
}
.select-hit-area .select-item {
  position: absolute;
  top: 8px;
  right: 8px;
}
.select-item.active {
  border-color: rgb(245, 155, 34);
  background-color: rgb(245, 155, 34);
}
.list-title {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  font-weight: bold;
  line-height: 16px;
  margin-right: 12px;
}
.status {
  font-size: 12px;
  line-height: 14px;
  color: rgb(1, 167, 240);
}
.status-tag {
  height: 16px;
  font-size: 11px;
  line-height: 16px;
  color: rgb(234, 88, 12);
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 8px;
  border: 1px solid rgb(243, 244, 246);
}
.info-item {
  border-top: 1px solid rgb(243, 244, 246);
}
.info-text {
  font-size: 12px;
  font-weight: bold;
  color: rgb(127, 127, 127);
}

.info-top {
  color: rgb(170, 170, 170);
}
.left-info {
  border-right: 1px solid rgb(243, 244, 246);
}
</style>
